/**
 * UsdbReserveCard - USDb Reserve summary card
 *
 * Displays the four key USDb reserve metrics:
 * Token ID, Circulating Supply, Total Allocation, and the USDC reserve on Hedera.
 *
 * @param {{ reserve: object | null }} props
 */

import React from "react";
import { colors } from "../../AgenticPaymentTransactionHistory/styles";
import { formatNumber } from "../utils";

const UsdbReserveCard = ({ reserve }) => {
    if (!reserve) return null;

    const cardStyle = {
        background: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: "8px",
        padding: "20px 24px",
        marginBottom: "20px",
    };

    const titleStyle = {
        margin: "0 0 16px 0",
        fontSize: "18px",
        fontWeight: 700,
        color: colors.primary,
        display: "flex",
        alignItems: "center",
        gap: "10px",
    };

    const statRowStyle = {
        display: "flex",
        gap: "20px",
        flexWrap: "wrap",
    };

    const statItemStyle = (bgColor) => ({
        flex: 1,
        minWidth: "160px",
        padding: "16px 20px",
        borderRadius: "8px",
        background: bgColor,
    });

    const statLabelStyle = {
        display: "block",
        fontSize: "11px",
        textTransform: "uppercase",
        fontWeight: 600,
        letterSpacing: "0.5px",
        marginBottom: "6px",
    };

    const statValueStyle = {
        display: "block",
        fontSize: "22px",
        fontWeight: 700,
    };

    return (
        <div style={cardStyle}>
            <h3 style={titleStyle}>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.primary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M12 10v4" />
                    <path d="M10 12h4" />
                </svg>
                USDb Reserve
            </h3>
            <div style={statRowStyle}>
                <div style={statItemStyle("#f0f4ff")} title="The ID used to identify USDb on the Hedera network">
                    <span style={{ ...statLabelStyle, color: colors.primary }}>
                        Token ID
                    </span>
                    <span
                        style={{
                            ...statValueStyle,
                            color: colors.primary,
                            fontSize: "16px",
                            fontFamily: "monospace",
                        }}
                    >
                        {reserve.tokenId || "-"}
                    </span>
                </div>
                <div style={statItemStyle("#f0fdf4")} title="The total amount of USDb currently held by all users">
                    <span style={{ ...statLabelStyle, color: "#16a34a" }}>
                        USDb Circulating Supply
                    </span>
                    <span style={{ ...statValueStyle, color: "#16a34a" }}>
                        {formatNumber(reserve.circulatingSupply, 3)}
                    </span>
                </div>
                <div style={statItemStyle("#eff6ff")} title="Every currency pool's USDb Allocation added up: all the currencies valued in USDb. Right once every pool is at its fair value">
                    <span style={{ ...statLabelStyle, color: "#2563eb" }}>
                        All Currencies in USDb
                    </span>
                    <span style={{ ...statValueStyle, color: "#2563eb" }}>
                        {formatNumber(reserve.totalUsdbAllocation, 3)}
                    </span>
                </div>
                <div style={statItemStyle("#f0fdf4")} title="The total USDC amount backing USDb, showing how much real money supports the reserve">
                    <span style={{ ...statLabelStyle, color: "#16a34a" }}>
                        USDC reserve on Hedera
                    </span>
                    <span style={{ ...statValueStyle, color: "#16a34a" }}>
                        ${formatNumber(reserve.totalReserveValue, 3)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default UsdbReserveCard;
